import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from "../../../../../../app/store";
import {BotIntegrations, ConnectIntegrationRequest} from "../../../../../../api/generated";
import {integrationApi, RequestStatus} from "../../../../../../api/apiConfiguration";
import {selectExceptionally} from "../../../../../../utils/selectExceptionally";
import {prepareEmails} from "../../../botCreateSlice";

export type ConnectIntegrationRequestPartial = Partial<
    Omit<ConnectIntegrationRequest, "superchatHandoverUserEmailList"> & { superchatHandoverUserEmailList: string }
>

interface ConnectIntegrationState {
    connectIntegrationRequest: ConnectIntegrationRequestPartial
    connectIntegrationRequestStatus: RequestStatus
}

const buildInitialState = () => {
    return {
        connectIntegrationRequest: {} as ConnectIntegrationRequestPartial,
        connectIntegrationRequestStatus: RequestStatus.NONE
    }
}

const initialState: ConnectIntegrationState = buildInitialState()

export const connectIntegration = createAsyncThunk<BotIntegrations, undefined, { state: RootState }>(
    'connectIntegration/connectIntegration',
    (_payload, {getState}) => {
        const request = getState().connectIntegration.connectIntegrationRequest
        return integrationApi.connectIntegration({connectIntegrationRequest: prepareConnectIntegrationRequest(request)})
    }
)

const prepareConnectIntegrationRequest = (request: ConnectIntegrationRequestPartial): ConnectIntegrationRequest => {
    return {
        publicBotId: selectExceptionally(request.publicBotId),
        integration: selectExceptionally(request.integration),
        superchatApiKey: request.superchatApiKey,
        superchatAimeUserEmail: request.superchatAimeUserEmail,
        superchatHandoverUserEmailList: prepareEmails(request.superchatHandoverUserEmailList),
    } as ConnectIntegrationRequest
}

export const connectIntegrationSlice = createSlice({
    name: 'connectIntegration',
    initialState,
    reducers: {
        clearConnectIntegrationState: () => {
            return buildInitialState();
        },
        updateConnectIntegrationRequest: (state, {payload}: PayloadAction<ConnectIntegrationRequestPartial>) => {
            state.connectIntegrationRequest = {
                ...state.connectIntegrationRequest,
                ...payload
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(connectIntegration.fulfilled, (state) => {
            state.connectIntegrationRequestStatus = RequestStatus.RESOLVED
        });
        builder.addCase(connectIntegration.pending, (state) => {
            state.connectIntegrationRequestStatus = RequestStatus.PENDING
        });
        builder.addCase(connectIntegration.rejected, (state) => {
            state.connectIntegrationRequestStatus = RequestStatus.REJECTED
        });
    }
})

export const {
    clearConnectIntegrationState,
    updateConnectIntegrationRequest
} = connectIntegrationSlice.actions;

export const connectIntegrationRequestSelector = (state: RootState): ConnectIntegrationRequestPartial => state.connectIntegration.connectIntegrationRequest;
export const connectIntegrationRequestStatusSelector = (state: RootState): RequestStatus => state.connectIntegration.connectIntegrationRequestStatus;
